import React, { useState } from 'react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

// Configure marked to output cleaner HTML
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

// Helper function to safely parse and sanitize markdown
const parseMarkdown = (text: string): string => {
  try {
    const result = marked.parse(text);
    return DOMPurify.sanitize(typeof result === 'string' ? result : '');
  } catch (error) {
    console.error('Markdown parsing error:', error);
    return DOMPurify.sanitize(text);
  }
};

interface RequirementInsight {
  requirementId: string;
  requirementTitle: string;
  requirementDescription?: string | null;
  requirementType: string;
  requirementPriority: string;
  insights: string;
  tokensUsed: number;
  error?: string;
}

interface RequirementsReviewData {
  projectId: string;
  projectName: string;
  requirementsProcessed: number;
  tokensUsed: number;
  model: string;
  results: RequirementInsight[];
}

interface RequirementsReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectName: string;
  reviewData: RequirementsReviewData | null;
  onRegenerate: () => void;
  onGenerateNew: () => void;
  isGenerating: boolean;
  requirementsCount?: number;
  generationCount?: number;
  requirementsWithoutInsightsCount?: number;
  readOnly?: boolean; // When true, hide regenerate/generate controls
}

export const RequirementsReviewModal: React.FC<RequirementsReviewModalProps> = ({
  isOpen,
  onClose,
  projectName,
  reviewData,
  onRegenerate,
  onGenerateNew,
  isGenerating,
  requirementsCount,
  generationCount,
  requirementsWithoutInsightsCount,
  readOnly
}) => {
  const [expandedRequirements, setExpandedRequirements] = useState<Set<string>>(new Set());

  if (!isOpen) return null;

  const toggleRequirement = (reqId: string) => {
    const newExpanded = new Set(expandedRequirements);
    if (newExpanded.has(reqId)) {
      newExpanded.delete(reqId);
    } else {
      newExpanded.add(reqId);
    }
    setExpandedRequirements(newExpanded);
  };

  const expandAll = () => {
    if (reviewData) {
      setExpandedRequirements(new Set(reviewData.results.map(r => r.requirementId)));
    }
  };

  const collapseAll = () => {
    setExpandedRequirements(new Set());
  };

  // Priority badge colors
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-8 border w-full max-w-6xl shadow-2xl rounded-lg bg-white mb-10">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex-1 flex gap-3">
            <svg className="w-12 h-12 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <div>
              <h2 className="text-2xl font-bold text-purple-900">
                Requirements Insights - {projectName}
              </h2>
              <p className="text-sm text-purple-600 mt-1">
                AI-Generated Enhancement Recommendations
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Loading State */}
        {isGenerating && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-purple-700 font-medium">
              {requirementsCount
                ? `Analyzing ${requirementsCount} requirement${requirementsCount !== 1 ? 's' : ''}...`
                : 'Generating insights for requirements...'}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {requirementsCount
                ? `Processing each requirement sequentially. Estimated time: ~${Math.ceil(requirementsCount * 3)} seconds`
                : 'This may take a minute for projects with many requirements'}
            </p>
            <div className="mt-4 text-xs text-purple-600 bg-purple-50 rounded-lg px-4 py-2 inline-block">
              Please wait while AI analyzes each requirement for potential improvements
            </div>
          </div>
        )}

        {/* Review Results */}
        {!isGenerating && reviewData && (
          <>
            {/* Metadata */}
            <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg p-4 mb-6">
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-blue-100 font-medium">Requirements Processed:</span>
                  <span className="ml-2 text-white">{reviewData.requirementsProcessed}</span>
                </div>
                <div>
                  <span className="text-blue-100 font-medium">Tokens Used:</span>
                  <span className="ml-2 text-white">{reviewData.tokensUsed.toLocaleString()}</span>
                </div>
                <div>
                  <span className="text-blue-100 font-medium">Model:</span>
                  <span className="ml-2 text-white">{reviewData.model}</span>
                </div>
                <div>
                  <span className="text-blue-100 font-medium">Regenerations:</span>
                  <span className={`ml-2 ${
                    (generationCount || 0) >= 5 ? 'text-red-200 font-bold' : 'text-white'
                  }`}>
                    {generationCount || 0}/5
                  </span>
                </div>
              </div>
            </div>

            {/* Expand/Collapse Controls */}
            <div className="flex justify-end gap-3 mb-4">
              <button
                onClick={expandAll}
                className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-full text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 transition-colors"
              >
                Expand All
              </button>
              <button
                onClick={collapseAll}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-full text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
              >
                Collapse All
              </button>
            </div>

            {/* Requirements List */}
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {reviewData.results.map((result) => {
                const isExpanded = expandedRequirements.has(result.requirementId);
                const hasError = !!result.error;

                return (
                  <div
                    key={result.requirementId}
                    className={`border rounded-lg overflow-hidden ${
                      hasError ? 'border-red-300 bg-red-50' : 'border-purple-200 bg-white'
                    }`}
                  >
                    {/* Requirement Header */}
                    <button
                      onClick={() => toggleRequirement(result.requirementId)}
                      className="w-full text-left p-4 hover:bg-purple-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {result.requirementTitle}
                            </h3>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getPriorityColor(result.requirementPriority)}`}>
                              {result.requirementPriority}
                            </span>
                            <span className="px-2 py-1 text-xs font-medium rounded bg-purple-100 text-purple-800">
                              {result.requirementType}
                            </span>
                          </div>
                          {result.requirementDescription && (
                            <p className="text-sm text-gray-600 mt-1 mb-2">
                              {result.requirementDescription}
                            </p>
                          )}
                          {hasError && (
                            <p className="text-sm text-red-600 mt-2">
                              ⚠️ {result.error}
                            </p>
                          )}
                        </div>
                        <svg
                          className={`w-5 h-5 text-purple-600 transition-transform ${
                            isExpanded ? 'transform rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </button>

                    {/* Insights Content */}
                    {isExpanded && !hasError && (
                      <div className="px-4 pb-4 border-t border-purple-200">
                        <div className="mt-4 prose prose-sm prose-purple max-w-none">
                          <div
                            className="text-gray-700 leading-relaxed"
                            dangerouslySetInnerHTML={{
                              __html: parseMarkdown(result.insights)
                            }}
                          />
                        </div>
                        <div className="mt-3 text-xs text-purple-600">
                          Tokens used: {result.tokensUsed}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center mt-6 pt-6 border-t">
              <div className="flex gap-3">
                {!readOnly && (
                  <>
                    {/* Regenerate All Button */}
                    <button
                      onClick={onRegenerate}
                      disabled={isGenerating || (generationCount || 0) >= 5}
                      className={`px-4 py-2 rounded-md font-medium transition-colors ${
                        (generationCount || 0) >= 5
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}
                      title={(generationCount || 0) >= 5 ? 'Generation limit reached (5/5)' : ''}
                    >
                      {isGenerating ? 'Regenerating...' : 'Regenerate All Insights'}
                    </button>

                    {/* Generate New Requirements Button - Only show if there are requirements without insights */}
                    {(requirementsWithoutInsightsCount || 0) > 0 && (
                      <button
                        onClick={onGenerateNew}
                        disabled={isGenerating}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400"
                      >
                        {isGenerating ? 'Generating...' : `Generate Insights for ${requirementsWithoutInsightsCount} New Requirement${requirementsWithoutInsightsCount !== 1 ? 's' : ''}`}
                      </button>
                    )}
                  </>
                )}
              </div>

              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </>
        )}

        {/* Empty State */}
        {!isGenerating && !reviewData && (
          <div className="text-center py-12">
            <p className="text-gray-600">No review data available</p>
          </div>
        )}
      </div>
    </div>
  );
};
